import React, { useContext, useEffect, useRef, useState } from 'react';
import { AccessibilityInfo, Animated, Easing, Image, Platform, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaInsetsContext } from 'react-native-safe-area-context';
import Svg, { Path } from 'react-native-svg';
import { useT } from '@transifex/react';
import { GLASS_CONTENT_LAYER, GlassBackdrop, Logo, Text, cn as dsCn, useTheme } from '@vidahealth/vida-ds';

import { testId } from '../../utils/testId';
import { BaseButton } from '../BaseButton';
import { useBrandSlides } from './slides';

const tid = (element: string) => testId('BrandCarousel', element);

const SLIDE_MS = 6000;
const FADE_MS = 600;
const TRACK_WIDTH = 36;
const useNativeDriver = Platform.OS !== 'web';

// Each screen mounts its own AuthLayout; carry the slide and a member's pause across navigations.
const session: { active: number; paused: boolean | null } = { active: 0, paused: null };
// Screens under a pushed one stay mounted, so a pause reaches every carousel, not just the visible one.
const pauseListeners = new Set<(paused: boolean) => void>();

const PlayPauseIcon = ({ paused, color }: { paused: boolean; color: string }) => (
  <Svg width={18} height={18} viewBox='0 0 18 18' fill='none'>
    <Path
      d={
        paused
          ? 'M5.25 3.9v10.2a.75.75 0 0 0 1.15.63l8.02-5.1a.75.75 0 0 0 0-1.26L6.4 3.27a.75.75 0 0 0-1.15.63Z'
          : 'M11.25 2.25H13.5c.41 0 .75.34.75.75v12c0 .41-.34.75-.75.75h-2.25a.75.75 0 0 1-.75-.75V3c0-.41.34-.75.75-.75ZM4.5 2.25h2.25c.41 0 .75.34.75.75v12c0 .41-.34.75-.75.75H4.5a.75.75 0 0 1-.75-.75V3c0-.41.34-.75.75-.75Z'
      }
      stroke={color}
      strokeWidth={1.5}
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </Svg>
);

type BrandCarouselProps = {
  // `panel` sits in the desktop/tablet container; `fullscreen` fills a phone behind the sheet.
  variant?: 'panel' | 'fullscreen';
  // Rendered under the slide copy, e.g. the phone entry's Get started / Log in buttons.
  footer?: React.ReactNode;
  // The slide's description under its title. The full-screen landing and short panels leave it out
  // so the copy stays in the dark band at the bottom.
  description?: boolean;
};

const INSETS = {
  panel: { top: 'inset-x-ds-48 top-ds-48', bottom: 'bottom-ds-48 left-ds-48 right-ds-48 max-w-[520px]' },
  fullscreen: { top: 'inset-x-ds-24 top-ds-24', bottom: 'bottom-ds-16 left-ds-24 right-ds-24' },
} as const;

export const BrandCarousel = ({
  variant = 'panel',
  footer,
  description = variant === 'panel',
}: BrandCarouselProps) => {
  const t = useT();
  const slides = useBrandSlides();
  const { colors } = useTheme();
  // Full screen on a phone sits under the status bar and home indicator. Read softly: no provider means no insets.
  const insets = useContext(SafeAreaInsetsContext);
  const safeTop = variant === 'fullscreen' ? (insets?.top ?? 0) : 0;
  const safeBottom = variant === 'fullscreen' ? (insets?.bottom ?? 0) : 0;
  const [active, setActive] = useState(session.active);
  const [paused, setPaused] = useState(session.paused ?? false);
  const progress = useRef(new Animated.Value(0)).current;
  const progressAtPause = useRef(0);
  const opacities = useRef(slides.map((_, i) => new Animated.Value(i === session.active ? 1 : 0))).current;

  useEffect(() => {
    if (session.paused !== null) return;
    AccessibilityInfo.isReduceMotionEnabled().then((reduce) => {
      if (reduce) setPaused(true);
    });
  }, []);

  useEffect(() => {
    pauseListeners.add(setPaused);
    return () => {
      pauseListeners.delete(setPaused);
    };
  }, []);

  useEffect(() => {
    session.active = active;
    session.paused = paused;
  }, [active, paused]);

  const togglePause = () => {
    const next = !paused;
    pauseListeners.forEach((listener) => listener(next));
  };

  useEffect(() => {
    if (paused) return;
    Animated.timing(progress, {
      toValue: 1,
      duration: SLIDE_MS * (1 - progressAtPause.current),
      easing: Easing.linear,
      useNativeDriver: false,
    }).start(({ finished }) => {
      if (!finished) return;
      progressAtPause.current = 0;
      progress.setValue(0);
      setActive((current) => (current + 1) % slides.length);
    });
    return () => progress.stopAnimation((value) => (progressAtPause.current = value));
  }, [active, paused, progress, slides.length]);

  useEffect(() => {
    Animated.parallel(
      opacities.map((opacity, i) =>
        Animated.timing(opacity, { toValue: i === active ? 1 : 0, duration: FADE_MS, useNativeDriver })
      )
    ).start();
  }, [active, opacities]);

  return (
    <View className='flex-1 bg-color-tidal' testID={tid('panel')}>
      {/* NativeWind doesn't style Animated.View, so layout classes sit on plain wrappers. */}
      {slides.map((slide, i) => (
        <View key={slide.key} className='absolute inset-0' aria-hidden>
          <Animated.View style={{ flex: 1, opacity: opacities[i] }}>
            {/* RN Web inlines the asset's intrinsic size, which beats className; style wins over it. */}
            <Image
              source={slide.image}
              resizeMode='cover'
              style={{ width: '100%', height: '100%' }}
              testID={tid(`image-${slide.key}`)}
            />
          </Animated.View>
        </View>
      ))}

      <View className='pointer-events-none absolute inset-x-0 top-0 h-[160px]'>
        <LinearGradient colors={[colors['scrim-media-tint'], colors['scrim-media-start']]} style={{ flex: 1 }} />
      </View>
      <View className='pointer-events-none absolute inset-x-0 bottom-0 h-1/2'>
        <LinearGradient colors={[colors['scrim-media-start'], colors['scrim-media-end']]} style={{ flex: 1 }} />
      </View>

      <View
        className={dsCn('absolute flex-row items-center justify-between', INSETS[variant].top)}
        style={{ marginTop: safeTop }}
      >
        <Logo variant='wordmark' width={120} color='color-swift' />
        <BaseButton
          onPress={togglePause}
          accessibilityLabel={paused ? t('Play slideshow') : t('Pause slideshow')}
          className='h-control-md w-control-md items-center justify-center overflow-hidden rounded-full bg-glass-on-dark'
          testID={tid('pause-button')}
        >
          <GlassBackdrop tone='on-dark' radius='rounded-full' />
          <View className={GLASS_CONTENT_LAYER}>
            <PlayPauseIcon paused={paused} color={colors['glass-on-dark-fg']} />
          </View>
        </BaseButton>
      </View>

      <View className={dsCn('absolute', INSETS[variant].bottom)} style={{ marginBottom: safeBottom }}>
        <View
          className={dsCn('flex-row items-center gap-ds-6', variant === 'fullscreen' ? 'mb-ds-16' : 'mb-ds-24')}
          aria-hidden
        >
          {slides.map((slide, i) =>
            i === active ? (
              <View key={slide.key} className='h-ds-6 overflow-hidden rounded-full' style={{ width: TRACK_WIDTH }}>
                <View className='absolute inset-0 bg-on-media-dark-foreground opacity-40' />
                <Animated.View
                  style={{
                    height: '100%',
                    width: progress.interpolate({ inputRange: [0, 1], outputRange: [0, TRACK_WIDTH] }),
                  }}
                >
                  <View className='flex-1 rounded-full bg-on-media-dark-foreground' />
                </Animated.View>
              </View>
            ) : (
              <View key={slide.key} className='h-ds-6 w-ds-6 rounded-full bg-on-media-dark-foreground opacity-40' />
            )
          )}
        </View>
        <View>
          {slides.map((slide, i) => (
            <View
              key={slide.key}
              className={dsCn(i !== active && 'absolute inset-x-0 top-0')}
              aria-hidden={i !== active}
            >
              {/* Photos crossfade; copy hands over in turn so two headlines never overlap. */}
              <Animated.View
                style={{ opacity: opacities[i]?.interpolate({ inputRange: [0, 0.5, 1], outputRange: [0, 0, 1] }) }}
              >
                <View className='gap-ds-16'>
                  <Text
                    variant={variant === 'fullscreen' ? 'h3' : 'h2'}
                    color='glass-on-dark'
                    testID={tid(`title-${slide.key}`)}
                  >
                    {slide.title}
                  </Text>
                  {description && (
                    <Text
                      variant='b2'
                      color='glass-on-dark'
                      className='max-w-[440px]'
                      testID={tid(`body-${slide.key}`)}
                    >
                      {slide.body}
                    </Text>
                  )}
                </View>
              </Animated.View>
            </View>
          ))}
        </View>
        {footer != null && <View className='mt-ds-24'>{footer}</View>}
      </View>
    </View>
  );
};
